class SideCart extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#SideCart-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.addEventListener('click', (evt) => {
      const navBtn = evt.target.closest('.side-cart__rec-btn');
      if (navBtn) {
        const scroll = this.querySelector('.scr-scroll');
        if (!scroll) return;
        const cardWidth = scroll.querySelector('.scr-card')?.offsetWidth || scroll.offsetWidth / 2;
        const amount = cardWidth + 16;
        scroll.scrollBy({ left: navBtn.dataset.dir === 'next' ? amount : -amount, behavior: 'smooth' });
        return;
      }

      const toggleBtn = evt.target.closest('.side-cart__rec-toggle');
      if (toggleBtn) {
        const rec = this.querySelector('.side-cart__recommendations');
        const collapsed = rec.classList.toggle('is-collapsed');
        toggleBtn.setAttribute('aria-expanded', String(!collapsed));
        return;
      }

      const featuredToggle = evt.target.closest('.side-cart__featured-toggle');
      if (featuredToggle) {
        const featured = featuredToggle.closest('.side-cart__featured');
        const collapsed = featured.classList.toggle('is-collapsed');
        featuredToggle.setAttribute('aria-expanded', String(!collapsed));
      }
    });

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source !== 'product-form') return;
      const featured = this.querySelector('.side-cart__featured');
      if (!featured) return;
      const variantId = String(event.productVariantId);
      const match = featured.querySelector(
        `[name="id"][value="${variantId}"], option[value="${variantId}"]`
      );
      if (match) this._collapseFeaturedAfterRender = true;
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const panelEl = this.querySelector('.side-cart__panel');
        if (panelEl) trapFocus(panelEl, panelEl.querySelector('.side-cart__close') || panelEl);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    document.documentElement.classList.add('overflow-hidden');

    const recProducts = this.querySelector('.side-cart__rec-products');
    const recSection = this.querySelector('.side-cart__recommendations');
    if (recProducts && recSection && !recProducts.querySelector('.scr-scroll')) {
      const productId = recSection.dataset.productId;
      if (productId) this.loadRecommendations(productId);
    }
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.documentElement.classList.remove('overflow-hidden');
  }

  renderContents(parsedState) {
    this.productId = parsedState.product_id || parsedState.id;

    // Snapshot old bar width before DOM swap so we can animate from it
    const oldBar = this.querySelector('.shipping-progress__bar');
    const oldBarWidth = oldBar ? oldBar.style.width : null;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionHTML = parsedState.sections?.[section.id];
      if (sectionElement && sectionHTML) {
        sectionElement.innerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
      }
    });

    if (this._collapseFeaturedAfterRender) {
      this._collapseFeaturedAfterRender = false;
      const featured = this.querySelector('.side-cart__featured');
      const rec = this.querySelector('.side-cart__recommendations');

      // Lock in the freshly rendered initial state so the max-height
      // transition has a "from" value to animate from.
      void this.offsetHeight;

      if (featured) {
        featured.classList.add('is-collapsed');
        const toggle = featured.querySelector('.side-cart__featured-toggle');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      }
      if (rec) {
        rec.classList.remove('is-collapsed');
        const recToggle = rec.querySelector('.side-cart__rec-toggle');
        if (recToggle) recToggle.setAttribute('aria-expanded', 'true');
      }
    }

    // If a bar existed before, snap the new bar back to the old width so the
    // CSS transition can animate forward to the updated value.
    if (oldBarWidth !== null) {
      const newBar = this.querySelector('.shipping-progress__bar');
      if (newBar) {
        newBar.style.transition = 'none';
        newBar.style.width = oldBarWidth;
        newBar.offsetWidth; // force reflow
        newBar.style.transition = '';
      }
    }

    // Always sync the bar with fresh cart data — handles both the case where
    // the sections API returned stale/null HTML and the animation trigger.
    fetch(`${routes.cart_url}.js`)
      .then((res) => res.json())
      .then((cartData) => {
        if (cartData.currency === 'CAD' || cartData.currency === 'USD') {
          this.updateShippingProgress(cartData);
        }
      })
      .catch(console.error);

    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: 'side-cart', selector: '#SideCart' },
      { id: 'cart-icon-bubble' },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  loadRecommendations(productId) {
    const container = this.querySelector('.side-cart__rec-products');
    if (!container) return;

    fetch(
      `/recommendations/products?section_id=side-cart-rec&product_id=${productId}&intent=related`
    )
      .then((res) => res.text())
      .then((html) => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        const scroll = tempDiv.querySelector('.scr-scroll');
        const recSection = container.closest('.side-cart__recommendations');

        // Strip old product-recommendations content if section hasn't synced
        tempDiv.querySelectorAll('.related-products__heading, .recWrapped ~ *:not(.recWrapped)').forEach((el) => el.remove());

        if (scroll) {
          container.innerHTML = '';
          container.appendChild(scroll);
          if (recSection) recSection.hidden = false;
        } else if (tempDiv.querySelector('.recWrapped')) {
          const oldGrid = tempDiv.querySelector('.recWrapped');
          const wrapper = document.createElement('div');
          wrapper.className = 'scr-scroll';
          wrapper.appendChild(oldGrid);
          container.innerHTML = '';
          container.appendChild(wrapper);
          if (recSection) recSection.hidden = false;
        } else {
          container.innerHTML = '';
          if (recSection) recSection.hidden = true;
        }
      })
      .catch(() => {
        container.innerHTML = '';
        const recSection = container.closest('.side-cart__recommendations');
        if (recSection) recSection.hidden = true;
      });
  }

  updateShippingProgress(cartData) {
    const shippingProgress = this.querySelector('.shipping-progress');
    if (!cartData || !shippingProgress) return;

    const currency = cartData.currency || shippingProgress.dataset.currency;
    if (currency !== 'CAD' && currency !== 'USD') {
      shippingProgress.remove();
      return;
    }
    const thresholdCents = parseInt(shippingProgress.dataset.threshold);
    if (!thresholdCents) {
      shippingProgress.remove();
      return;
    }
    const cartTotalCents = cartData.total_price || 0;
    const cartTotal = cartTotalCents / 100;
    const shippingThresholdAmount = thresholdCents / 100;

    let progressPercentage = (cartTotal / shippingThresholdAmount) * 100;
    if (progressPercentage > 100) progressPercentage = 100;

    const hasFreeShipping = cartTotal >= shippingThresholdAmount;
    const remainingAmount = hasFreeShipping ? 0 : shippingThresholdAmount - cartTotal;

    const progressBar = shippingProgress.querySelector('.shipping-progress__bar');
    if (progressBar) {
      progressBar.style.width = `${progressPercentage}%`;
      progressBar.classList.toggle('shipping-progress__bar--complete', hasFreeShipping);
    }

    shippingProgress.classList.toggle('shipping-progress--complete', hasFreeShipping);

    const milestone = shippingProgress.querySelector('.shipping-progress__milestone');
    if (milestone) {
      milestone.classList.toggle('shipping-progress__milestone--achieved', hasFreeShipping);
      // Use a zero-width space when empty so div:empty never matches
      milestone.innerHTML = hasFreeShipping
        ? `<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 6l3 3 5-5"/></svg>`
        : '\u200B';
    }

    const messageText = shippingProgress.querySelector('.shipping-progress__text');
    if (messageText) {
      if (hasFreeShipping) {
        messageText.textContent = "You've unlocked free shipping!";
        messageText.classList.add('shipping-progress__text--success');
      } else {
        const remainingFormatted = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency,
          minimumFractionDigits: 2,
        }).format(remainingAmount);
        messageText.textContent = `Add ${remainingFormatted} more for free shipping`;
        messageText.classList.remove('shipping-progress__text--success');
      }
    }

    shippingProgress.dataset.cartTotal = cartTotalCents.toString();
    shippingProgress.dataset.currency = currency;
  }
}

customElements.define('side-cart', SideCart);

class SideCartItems extends CartItems {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('SideCart-LineItemStatus') || this.lineItemStatusElement;
  }

  getSectionsToRender() {
    return [
      { id: 'SideCart', section: 'side-cart', selector: '.side-cart__panel' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }

  updateQuantity(line, quantity, _name, variantId) {
    if (quantity === 0) {
      const item = document.getElementById(`SideCart-Item-${line}`);
      if (item) item.classList.add('is-removing');
    }
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    // Snapshot bar width before the fetch so we can animate from it later
    const sideCartWrapperRef = document.querySelector('side-cart');
    const oldBar = sideCartWrapperRef?.querySelector('.shipping-progress__bar');
    const oldBarWidth = oldBar ? oldBar.style.width : null;

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement = document.getElementById(`SideCart-qty-${line}`);

        if (parsedState.errors) {
          if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const sideCartWrapper = document.querySelector('side-cart');
        if (sideCartWrapper) {
          sideCartWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
        }

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) ||
            document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });

        // Snap the newly rendered bar back to the old width so the CSS
        // transition can animate forward when updateShippingProgress fires.
        if (oldBarWidth !== null && sideCartWrapper) {
          const newBar = sideCartWrapper.querySelector('.shipping-progress__bar');
          if (newBar) {
            newBar.style.transition = 'none';
            newBar.style.width = oldBarWidth;
            newBar.offsetWidth; // force reflow
            newBar.style.transition = '';
          }
        }

        if (sideCartWrapper) {
          const recSection = sideCartWrapper.querySelector('.side-cart__recommendations');
          const recProducts = sideCartWrapper.querySelector('.side-cart__rec-products');
          if (recSection && recProducts) {
            const newProductId = recSection.dataset.productId;
            if (newProductId) {
              sideCartWrapper.loadRecommendations(newProductId);
            }
          }
        }

        if (sideCartWrapper) {
          fetch(`${routes.cart_url}.js`)
            .then((res) => res.json())
            .then((cartData) => {
              if (cartData.currency === 'CAD' || cartData.currency === 'USD') {
                sideCartWrapper.updateShippingProgress(cartData);
              } else {
                const shippingProgress = sideCartWrapper.querySelector('.shipping-progress');
                if (shippingProgress) shippingProgress.remove();
              }
            });
        }

        const updatedValue = parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined;
        let message = '';
        const items = document.querySelectorAll('.side-cart__item');
        if (
          items.length === parsedState.items.length &&
          quantityElement &&
          updatedValue !== parseInt(quantityElement.value)
        ) {
          message =
            typeof updatedValue === 'undefined'
              ? window.cartStrings.error
              : window.cartStrings.quantityError.replace('[quantity]', updatedValue);
        }
        this.updateLiveRegions(line, message);

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: parsedState,
          variantId,
        });
      })
      .catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) =>
          overlay.classList.add('hidden')
        );
        const errors = document.getElementById('SideCart-CartErrors');
        if (errors) {
          errors.textContent = window.cartStrings.error;
          errors.hidden = false;
        }
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=side-cart`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        for (const selector of ['side-cart-items', '.side-cart__footer', '.shipping-progress']) {
          const target = document.querySelector(selector);
          const source = html.querySelector(selector);
          if (target && source) target.replaceWith(source);
        }
      })
      .catch((e) => console.error(e));
  }

  enableLoading(line) {
    const cartItemsEl = document.getElementById('SideCart-CartItems');
    if (cartItemsEl) cartItemsEl.classList.add('cart__items--disabled');

    this.querySelectorAll(`#SideCart-Item-${line} .loading-overlay`).forEach((overlay) =>
      overlay.classList.remove('hidden')
    );

    document.activeElement.blur();
    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const cartItemsEl = document.getElementById('SideCart-CartItems');
    if (cartItemsEl) cartItemsEl.classList.remove('cart__items--disabled');

    this.querySelectorAll(`#SideCart-Item-${line} .loading-overlay`).forEach((overlay) =>
      overlay.classList.add('hidden')
    );
  }

  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`SideCart-LineItemError-${line}`);
    if (lineItemError) {
      const errorText = lineItemError.querySelector('.cart-item__error-text');
      if (errorText) errorText.innerHTML = message;
      lineItemError.hidden = !message;
    }

    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('SideCart-LiveRegionText');
    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', false);
      setTimeout(() => cartStatus.setAttribute('aria-hidden', true), 1000);
    }
  }
}

customElements.define('side-cart-items', SideCartItems);

class SideCartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('side-cart-items');
      if (cartItems) cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('side-cart-remove-button', SideCartRemoveButton);

class SideCartAddon extends HTMLElement {
  constructor() {
    super();
    this.checkbox = this.querySelector('.side-cart__addon-checkbox');
    this.variantId = this.dataset.variantId;
    if (!this.checkbox || !this.variantId) return;
    this.checkbox.addEventListener('change', this.onToggle.bind(this));
  }

  onToggle() {
    const wasChecked = this.checkbox.checked;
    this.checkbox.disabled = true;
    this.classList.add('is-loading');

    const cartItems = document.querySelector('side-cart-items');
    const sectionsToRender = cartItems
      ? cartItems.getSectionsToRender()
      : [
          { id: 'SideCart', section: 'side-cart', selector: '.side-cart__panel' },
          { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
        ];
    const sections = sectionsToRender.map((s) => s.section);

    const endpoint = wasChecked ? routes.cart_add_url : routes.cart_change_url;
    const body = wasChecked
      ? {
          items: [{ id: this.variantId, quantity: 1 }],
          sections,
          sections_url: window.location.pathname,
        }
      : {
          id: this.variantId,
          quantity: 0,
          sections,
          sections_url: window.location.pathname,
        };

    fetch(endpoint, { ...fetchConfig(), body: JSON.stringify(body) })
      .then((res) => res.json())
      .then((data) => {
        if (data.status && data.status !== 200) {
          this.checkbox.checked = !wasChecked;
          return;
        }

        const sideCartWrapper = document.querySelector('side-cart');
        const itemCount = data.item_count != null ? data.item_count : null;
        if (sideCartWrapper && itemCount != null) {
          sideCartWrapper.classList.toggle('is-empty', itemCount === 0);
        }
        if (cartItems && itemCount != null) {
          cartItems.classList.toggle('is-empty', itemCount === 0);
        }

        if (data.sections) {
          sectionsToRender.forEach((section) => {
            const sectionHTML = data.sections[section.section];
            if (!sectionHTML) return;
            const container = document.getElementById(section.id);
            if (!container) return;
            const elementToReplace = container.querySelector(section.selector) || container;
            elementToReplace.innerHTML = cartItems
              ? cartItems.getSectionInnerHTML(sectionHTML, section.selector)
              : new DOMParser()
                  .parseFromString(sectionHTML, 'text/html')
                  .querySelector(section.selector).innerHTML;
          });
        }

        if (sideCartWrapper) {
          fetch(`${routes.cart_url}.js`)
            .then((res) => res.json())
            .then((cartData) => {
              if (cartData.currency === 'CAD' || cartData.currency === 'USD') {
                sideCartWrapper.updateShippingProgress(cartData);
              }
            })
            .catch(() => {});
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'side-cart-addon',
          cartData: data,
          variantId: this.variantId,
        });
      })
      .catch(() => {
        this.checkbox.checked = !wasChecked;
        const errors = document.getElementById('SideCart-CartErrors');
        if (errors) {
          errors.textContent = window.cartStrings && window.cartStrings.error;
          errors.hidden = false;
        }
      })
      .finally(() => {
        this.checkbox.disabled = this.dataset.unavailable === 'true';
        this.classList.remove('is-loading');
      });
  }
}

customElements.define('side-cart-addon', SideCartAddon);
